import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Shield, Check, FileText, ChevronLeft, Loader2, Phone } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import AccessibilityWidget from './AccessibilityWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const THERMOSTAT_IMG = "https://customer-assets.emergentagent.com/job_cooling-converter/artifacts/kvo2sll9_ProStat_PRODUCT-Details_720x550_PRS4110-PRS4210%281%29.jpg";

const formatPrice = (price) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(price);

export default function InstallationProductPage({ onRequestQuote, onOpenLeadForm, onOpenEstimateForm }) {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/products/${id}`).then(r => setProduct(r.data)).catch(() => setProduct(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header onOpenLeadForm={onOpenLeadForm} onOpenEstimateForm={onOpenEstimateForm} />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="ml-3 text-gray-600">Loading product...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header onOpenLeadForm={onOpenLeadForm} onOpenEstimateForm={onOpenEstimateForm} />
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <Link to="/installation" className="text-emerald-600 hover:text-emerald-700 font-medium">Back to Installation products</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = [
    ...(product.install_images?.length > 0
      ? product.install_images.map((url, i) => ({ src: url, alt: `${product.name} - Image ${i + 1}` }))
      : product.image_url ? [{ src: product.image_url, alt: product.name }] : []),
    { src: THERMOSTAT_IMG, alt: "ProStat Digital Thermostat Included" }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="install-product-page">
      <Header onOpenLeadForm={onOpenLeadForm} onOpenEstimateForm={onOpenEstimateForm} />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <Link to="/installation" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-emerald-600 transition-colors mb-8" data-testid="back-to-installation">
          <ChevronLeft className="w-4 h-4" /> Back to Installation products
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-8 aspect-square border border-gray-100 mb-3">
              <img src={images[activeImage]?.src} alt={images[activeImage]?.alt} className="w-full h-full object-contain" data-testid="install-page-image" />
            </div>
            {images.length > 1 && (
              <div className="flex gap-3" data-testid="install-page-thumbnails">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImage(i)} className={`w-20 h-20 rounded-lg border-2 overflow-hidden flex items-center justify-center bg-gray-50 transition-all ${activeImage === i ? 'border-emerald-500 ring-1 ring-emerald-500' : 'border-gray-200 hover:border-gray-300'}`} data-testid={`install-page-thumb-${i}`}>
                    <img src={img.src} alt={img.alt} className="w-full h-full object-contain p-1" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <span className="inline-block bg-emerald-500 text-white text-xs font-semibold uppercase tracking-wide px-3 py-1.5 rounded-md mb-4">AC Installation Near Me</span>
            <h1 className="text-3xl font-bold text-gray-900 leading-snug mb-3" data-testid="install-page-title">{product.name}</h1>
            <p className="text-gray-400 text-sm mb-4">Model: {product.model_number}</p>

            <div className="flex items-center gap-2 mb-6">
              <Shield className="w-5 h-5 text-emerald-600" />
              <span className="text-emerald-600 font-semibold text-sm">10 Year Factory Warranty</span>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                <p className="text-xs text-gray-400 mb-0.5">Tonnage</p>
                <p className="font-bold text-gray-900">{product.tonnage}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                <p className="text-xs text-gray-400 mb-0.5">Efficiency</p>
                <p className="font-bold text-gray-900">{product.seer_rating}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                <p className="text-xs text-gray-400 mb-0.5">Refrigerant</p>
                <p className="font-bold text-gray-900">{product.refrigerant}</p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">What's Included</h3>
              <div className="grid grid-cols-2 gap-2">
                {[...product.features, '10 Year Factory Warranty'].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600" data-testid={`whats-included-item-${i}`}>
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6">
              <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-5" data-testid="install-page-price">
                <p className="font-bold text-gray-900 text-lg mb-2">{product.name}</p>
                <p className="text-gray-700 text-sm leading-relaxed">
                  No Money Down, No Payments for 6 months, No Pre Payment Penalty... just <span className="font-bold text-emerald-700 text-lg">{formatPrice(product.installed_monthly)}/mo</span> W.A.C
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-3">
                  {formatPrice(product.installed_price)} <span className="text-base font-semibold text-gray-600">Installed</span>
                </p>
              </div>

              <button
                onClick={() => onRequestQuote(product)}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-4 font-semibold rounded-lg transition-colors text-base flex items-center justify-center gap-2"
                data-testid="install-page-quote-btn"
              >
                <FileText className="w-5 h-5" />
                Schedule Installation
              </button>

              <div className="mt-5 text-center">
                <a href="tel:+18442035519" className="hidden">
                  Questions? Call 1-844-203-5519
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <AccessibilityWidget />
    </div>
  );
}
